/**
 * Obstruction wizard — step validation (single source of step order + logic)
 *
 * Component-free per-step validators plus `OBSTRUCTION_STEP_LOGIC`, the ordered
 * `{ id, requiredFields, validate }` list that defines the steps. Navigation /
 * submission hooks index this list by position so `currentStep` stays purely
 * positional. The presentation half (icon/label/render) is zipped on in
 * `config/obstructionStepDescriptors.tsx`.
 *
 * Validation here covers ONLY "can I leave this step" rules. Stricter checks
 * (max-length, regex etc.) live in `useObstructionForm` (Mantine form schema).
 */
import {
  ObstructionType,
  SeverityLevel,
  TrafficImpact,
} from '@/types/obstructions';
import {
  validateRequiredFields,
  type StepValidationContext,
  type StepValidationResult,
} from '@/components/common/wizard';

import type {
  ObstructionWizardFormState,
  ObstructionWizardStepKey,
} from '../../types';

/**
 * Per-step enum-membership guards (defence-in-depth — Select keeps these typed).
 * Field absent from the map → no enum check.
 */
const ENUM_FIELD_GUARDS: Partial<
  Record<keyof ObstructionWizardFormState, (value: unknown) => boolean>
> = {
  type: (value) =>
    Object.values(ObstructionType).includes(value as ObstructionType),
  severity: (value) =>
    Object.values(SeverityLevel).includes(value as SeverityLevel),
  impact: (value) =>
    Object.values(TrafficImpact).includes(value as TrafficImpact),
};

/**
 * Required-field check (shared `validateRequiredFields`) plus per-field
 * enum-membership guards layered on the fields that pass it.
 */
function evaluateRequiredFields(
  requiredFields: (keyof ObstructionWizardFormState)[],
  values: ObstructionWizardFormState,
  formErrors: Partial<Record<keyof ObstructionWizardFormState, string>>
): Record<string, string> {
  const { errors } = validateRequiredFields(requiredFields, values, formErrors);

  for (const field of requiredFields) {
    if (errors[field]) continue;
    const enumGuard = ENUM_FIELD_GUARDS[field];
    if (enumGuard && !enumGuard(values[field])) {
      errors[field] = 'Field required';
    }
  }

  return errors;
}

type ObstructionStepContext = StepValidationContext<ObstructionWizardFormState>;

const BASIC_INFO_REQUIRED: (keyof ObstructionWizardFormState)[] = [
  'title',
  'type',
  'severity',
  'plannedStartDate',
  'plannedEndDate',
  'impact',
];

const CONTRACTOR_REQUIRED: (keyof ObstructionWizardFormState)[] = [
  'contractorCompany',
  'contractorManager',
  'contractorPhone',
];

const AFFECTED_ROADS_REQUIRED: (keyof ObstructionWizardFormState)[] = [];

const ATTACHMENTS_REQUIRED: (keyof ObstructionWizardFormState)[] = [];

function validateBasicInfoStep({
  values,
  formErrors,
}: ObstructionStepContext): StepValidationResult {
  const errors = evaluateRequiredFields(
    BASIC_INFO_REQUIRED,
    values,
    formErrors
  );

  // Cross-field: end > start (schedule now lives on the basic-info step).
  const { plannedStartDate: start, plannedEndDate: end } = values;
  if (start && end) {
    const startMs = Date.parse(start);
    const endMs = Date.parse(end);
    if (
      Number.isFinite(startMs) &&
      Number.isFinite(endMs) &&
      endMs <= startMs
    ) {
      errors.plannedEndDate = 'End must be after start';
    }
  }

  return { isValid: Object.keys(errors).length === 0, errors };
}

function validateContractorStep({
  values,
  formErrors,
}: ObstructionStepContext): StepValidationResult {
  const errors = evaluateRequiredFields(
    CONTRACTOR_REQUIRED,
    values,
    formErrors
  );
  return { isValid: Object.keys(errors).length === 0, errors };
}

function validateAffectedRoadsStep(): StepValidationResult {
  // Affected roads are optional and gated individually at add-time; the list
  // itself never blocks navigation or submission.
  return { isValid: true, errors: {} };
}

function validateAttachmentsStep(): StepValidationResult {
  // Attachments are optional; oversized/unsupported files are rejected by the
  // FileUpload drop zone itself, so the step never blocks navigation.
  return { isValid: true, errors: {} };
}

/** Validation half of an obstruction step (no presentation / component import). */
export interface ObstructionStepLogic {
  id: ObstructionWizardStepKey;
  requiredFields: (keyof ObstructionWizardFormState)[];
  validate: (context: ObstructionStepContext) => StepValidationResult;
}

/** Ordered section logic — fixed 4 sections. */
export const OBSTRUCTION_STEP_LOGIC: ObstructionStepLogic[] = [
  {
    id: 'basicInfo',
    requiredFields: BASIC_INFO_REQUIRED,
    validate: validateBasicInfoStep,
  },
  {
    id: 'contractor',
    requiredFields: CONTRACTOR_REQUIRED,
    validate: validateContractorStep,
  },
  {
    id: 'affectedRoads',
    requiredFields: AFFECTED_ROADS_REQUIRED,
    validate: validateAffectedRoadsStep,
  },
  {
    id: 'attachments',
    requiredFields: ATTACHMENTS_REQUIRED,
    validate: validateAttachmentsStep,
  },
];
